import {
  EnumProductAddOnSettingsBusinessScope,
  EnumProductCustomFieldsType,
  Product,
} from "gql/generated";
import { formatPrice, getMultilingualString } from "shared/utils";
import { useTranslation } from "react-i18next";
import { Flex } from "styled-system/jsx";
import { useEffect, useMemo } from "react";
import {
  BusinessScopedFieldGroup,
  SurveyQuestionInput,
} from "shared/components/organisms/SurveyQuestionInput";
import { useBookerContext, useCartContext } from "widgets/booker/contexts";
import { UpsellSelectionCard } from "widgets/booker/shared";
import type {
  CartBusinessScope,
  CartCustomFieldResponse,
} from "widgets/booker/contexts/CartContextProvider/cartContextTypes";
import {
  alignToSelection,
  countChargedChoices,
  perBusinessQuantity,
} from "@shared/businessScope";
import {
  getAddOnCustomFields,
  setCustomFieldResponse,
  toSurveyQuestion,
} from "./addOnCustomFields";

type AddOnItemProps = {
  addOn: Product | undefined;
};

export const AddOnItem = ({ addOn }: AddOnItemProps) => {
  const {
    cart,
    addCartItem,
    removeCartItem,
    updateCartItem,
    updateCartItemDetails,
    businessField,
  } = useCartContext();
  const { language, booker } = useBookerContext();
  const { t } = useTranslation("booker", { keyPrefix: "ADD_ONS" });

  const businesses = useMemo(
    () =>
      (businessField.includedBusinesses ?? []).map(b => ({
        operatorId: b.operatorEntityId,
        businessName: b.businessName,
      })),
    [businessField.includedBusinesses],
  );
  const selectedItem = addOn
    ? cart.find(item => item.productId === addOn._id)
    : undefined;
  const isPerBusiness =
    !!booker?.perBusinessAnswersEnabled &&
    addOn?.addOnSettings?.businessScope ===
      EnumProductAddOnSettingsBusinessScope.PerBusiness &&
    businesses.length > 1;
  const pricingScope = addOn?.addOnSettings?.pricingScope;
  // Checkbox groups that bill one unit per choice ticked.
  const chargedFieldIds = booker?.perBusinessAnswersEnabled
    ? getAddOnCustomFields(addOn)
        .filter(
          field =>
            field.chargePerChoice &&
            field.type === EnumProductCustomFieldsType.Boolean &&
            (field.options?.length ?? 0) > 0,
        )
        .map(field => String(field._id))
    : [];
  const chargesPerChoice = chargedFieldIds.length > 0;
  // Charged per choice: nothing is billed until a choice is ticked.
  const quantityForScopes = (scopes: CartBusinessScope[]) =>
    chargesPerChoice
      ? scopes.reduce(
          (sum, scope) =>
            sum + countChargedChoices(chargedFieldIds, scope.customFieldResponses),
          0,
        )
      : perBusinessQuantity(pricingScope, scopes.length);
  const quantityForAnswers = (answers: CartCustomFieldResponse[] | undefined) =>
    countChargedChoices(chargedFieldIds, answers);

  // Follow the business selection: drop businesses that were removed, and
  // fall back to a single set of answers when only one business is left.
  const businessKey = businesses.map(b => b.operatorId).join(",");
  useEffect(() => {
    if (!addOn || !selectedItem) return;
    const scopes = selectedItem.businessScopes;
    if (!isPerBusiness) {
      if (scopes?.length) {
        updateCartItem(addOn._id, {
          businessScopes: undefined,
          quantity: chargesPerChoice
            ? quantityForAnswers(selectedItem.customFieldResponses)
            : 1,
        });
      }
      return;
    }
    const aligned = alignToSelection(
      businesses.map(b => b.operatorId),
      scopes?.length ? scopes : businesses.map(b => ({ ...b })),
    );
    if (aligned.length === 0) {
      removeCartItem(addOn._id);
      return;
    }
    if (
      aligned.length !== (scopes?.length ?? 0) ||
      aligned.some((scope, i) => scope.operatorId !== scopes?.[i]?.operatorId)
    ) {
      updateCartItem(addOn._id, {
        businessScopes: aligned,
        quantity: quantityForScopes(aligned),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessKey, isPerBusiness, !!selectedItem]);

  if (!addOn) {
    return null;
  }

  const price = addOn.pricing?.tiers[0]?.price ?? 0;
  const unitPriceLabel = `$${formatPrice(price)}`;
  const priceLabel =
    price === 0
      ? t("FREE")
      : !chargesPerChoice
        ? unitPriceLabel
        : selectedItem
          ? t("PRICE_PER_CHOICE_TOTAL", {
              price: unitPriceLabel,
              count: selectedItem.businessScopes?.length
                ? selectedItem.businessScopes.reduce(
                    (sum, scope) =>
                      sum +
                      countChargedChoices(chargedFieldIds, scope.customFieldResponses),
                    0,
                  )
                : countChargedChoices(chargedFieldIds, selectedItem.customFieldResponses),
            })
          : t("PRICE_PER_CHOICE", { price: unitPriceLabel });

  const isSelected = !!selectedItem;
  const customFields = getAddOnCustomFields(addOn);

  const cartLine = (quantity: number, businessScopes?: CartBusinessScope[]) => ({
    productId: addOn._id,
    productName: getMultilingualString(addOn.name, language),
    productType: addOn.type,
    endDateISO: "",
    startDateISO: "",
    guestsPerTier: [],
    total: price * quantity,
    quantity,
    ...(businessScopes ? { businessScopes } : {}),
  });

  const handleQuantityAddOnChange = (quantity: number) => {
    if (quantity === 0) {
      removeCartItem(addOn._id);
    } else if (isSelected) {
      updateCartItem(addOn._id, { quantity });
    } else {
      addCartItem(cartLine(quantity));
    }
  };

  const handleToggleAddOn = (checked: boolean) => {
    if (!checked) {
      removeCartItem(addOn._id);
      return;
    }
    if (isPerBusiness) {
      const scopes = businesses.map(b => ({ ...b }));
      addCartItem(cartLine(quantityForScopes(scopes), scopes));
    } else {
      addCartItem(cartLine(chargesPerChoice ? 0 : 1));
    }
  };

  const handleResponseChange = (fieldId: string, response: string[]) => {
    if (!selectedItem) return;
    const next = setCustomFieldResponse(
      selectedItem.customFieldResponses,
      fieldId,
      response,
    );
    if (chargedFieldIds.includes(fieldId)) {
      updateCartItem(addOn._id, {
        customFieldResponses: next,
        quantity: quantityForAnswers(next),
      });
    } else {
      updateCartItemDetails(addOn._id, next);
    }
  };

  const scopes = selectedItem?.businessScopes ?? [];

  const handleBusinessCheckedChange = (operatorId: string, checked: boolean) => {
    const next = checked
      ? alignToSelection(
          businesses.map(b => b.operatorId),
          [
            ...scopes,
            ...businesses.filter(b => b.operatorId === operatorId),
          ],
        )
      : scopes.filter(scope => scope.operatorId !== operatorId);
    if (next.length === 0) {
      removeCartItem(addOn._id);
      return;
    }
    updateCartItem(addOn._id, {
      businessScopes: next,
      quantity: quantityForScopes(next),
    });
  };

  const handleBusinessResponseChange = (
    operatorId: string,
    fieldId: string,
    response: string[],
  ) => {
    if (!selectedItem) return;
    const next = scopes.map(scope =>
      scope.operatorId === operatorId
        ? {
            ...scope,
            customFieldResponses: setCustomFieldResponse(
              scope.customFieldResponses,
              fieldId,
              response,
            ),
          }
        : scope,
    );
    if (chargedFieldIds.includes(fieldId)) {
      updateCartItem(addOn._id, {
        businessScopes: next,
        quantity: quantityForScopes(next),
      });
    } else {
      updateCartItemDetails(addOn._id, selectedItem.customFieldResponses, next);
    }
  };

  const renderFields = (
    answers: { fieldId: string; response: string[] }[] | undefined,
    onChange: (fieldId: string, response: string[]) => void,
    keySuffix = "",
  ) =>
    customFields.map(field => (
      <SurveyQuestionInput
        key={`${String(field._id)}${keySuffix}`}
        idScope={`${String(field._id)}${keySuffix}`}
        maxChoices={field.maxChoices}
        question={toSurveyQuestion(field)}
        description={
          field.description
            ? getMultilingualString(field.description, language) || undefined
            : undefined
        }
        response={
          answers?.find(answer => answer.fieldId === String(field._id))
            ?.response ?? []
        }
        onResponseChange={onChange}
      />
    ));

  const footer = !isSelected
    ? undefined
    : isPerBusiness
      ? (
          <BusinessScopedFieldGroup
            businesses={businesses}
            checkedOperatorIds={scopes.map(scope => scope.operatorId)}
            onCheckedChange={handleBusinessCheckedChange}
            renderFields={
              customFields.length > 0
                ? operatorId =>
                    renderFields(
                      scopes.find(scope => scope.operatorId === operatorId)
                        ?.customFieldResponses,
                      (fieldId, response) =>
                        handleBusinessResponseChange(operatorId, fieldId, response),
                      `-${operatorId}`,
                    )
                : undefined
            }
          />
        )
      : customFields.length > 0
        ? (
            <Flex flexDirection="column" gap="200">
              {renderFields(selectedItem.customFieldResponses, handleResponseChange)}
            </Flex>
          )
        : undefined;

  return (
    <UpsellSelectionCard
      key={addOn._id}
      mainText={getMultilingualString(addOn.name, language)}
      subText={priceLabel}
      checked={isSelected}
      onCheckedChange={v => handleToggleAddOn(v)}
      quantityEnabled={
        !isPerBusiness && !chargesPerChoice && !!addOn.quantitySettings?.enabled
      }
      maxQuantity={
        addOn.quantitySettings?.maxQuantity
          ? addOn.quantitySettings.maxQuantity
          : undefined
      }
      quantity={selectedItem?.quantity}
      onQuantityChange={v => handleQuantityAddOnChange(v)}
      footer={footer}
    />
  );
};
